import { BOOKING_STATES } from 'constants/bookings';
import {
  getMaxDate,
  getMinDate,
  type RecurrenceFormOptions,
} from 'utils/booking';
import moment from 'utils/dayjs';
import { i18n as I18n } from 'utils/i18n';
import { createGetValidator } from 'utils/validation';
import * as yup from 'yup';

export const recurrenceTypes = ['daily', 'weekly', 'monthly'] as const;
export const dayRanges = ['days', 'weekdays'] as const;
export const monthRanges = ['nthDayOfMonth', 'nthWeekdayOfMonth'] as const;
export const rangeEndTypes = ['date', 'count'] as const;

const getSchema = () =>
  yup.object({
    id: yup.number(),
    date: yup
      .date()
      .required(I18n.t('bookings.errors.date_not_selected'))
      // yup's ConditionBuilder types the resolved values as any[], so the tuples
      // below use optional elements (a fixed-length tuple is not assignable).
      .when(
        ['service', '$services', '$date', '$state'],
        (
          [
            service,
            services = [],
            today = moment().toDate(),
            state = BOOKING_STATES.tentative,
          ]: [
            number?,
            Array<{ id: number; ccgCommissioned?: boolean }>?,
            Date?,
            string?,
            ...unknown[],
          ],
          schema: yup.DateSchema
        ) => {
          if (!service || state !== BOOKING_STATES.tentative) return schema;

          return schema.max(
            getMaxDate(
              today,
              services.find(({ id }) => id === service)
            ),
            ({ max }) => I18n.t('bookings.errors.max_date', { max })
          );
        }
      ),
    service: yup
      .number()
      .required(I18n.t('bookings.errors.service_not_selected')),
    host: yup.object({ id: yup.number() }),
    slots: yup.array().of(
      yup.object({
        startTime: yup.string(),
        endTime: yup.string(),
      })
    ),
    recurrence: yup
      .object()
      .when(
        ['date', 'service', '$services', '$date', '$state'],
        (
          [
            date = moment().toDate(),
            service,
            services = [],
            today = moment().toDate(),
            state = BOOKING_STATES.tentative,
          ]: [
            Date?,
            number?,
            Array<{ id: number; ccgCommissioned?: boolean }>?,
            Date?,
            string?,
            ...unknown[],
          ],
          schema: yup.ObjectSchema<yup.AnyObject>,
          {
            value: recurrence = {},
          }: { value?: RecurrenceFormOptions & { enabled?: boolean } }
        ) => {
          if (!recurrence.enabled || state !== BOOKING_STATES.tentative)
            return schema;

          return schema.shape({
            type: yup
              .string()
              .required()
              .label('Recurrence type') // TODO: i18n
              .oneOf([...recurrenceTypes]),
            enabled: yup.boolean(),
            checked: yup.boolean(),
            everyNumberOf: yup.number().min(1),
            dayRange: yup.string().oneOf([...dayRanges]),
            weekDays: yup.array().of(yup.string()),
            dayOfMonthRange: yup.string().oneOf([...monthRanges]),
            nthDayOfMonth: yup.number().min(1).max(31),
            weekNumber: yup.number(),
            weekDay: yup.string(),
            rangeEndType: yup.string().oneOf([...rangeEndTypes]),
            endDate: yup
              .date()
              .when(['rangeEndType'], ([rangeEndType], schema) => {
                if (rangeEndType !== 'date') return schema;

                return schema
                  .min(getMinDate(date, recurrence), ({ min }) =>
                    I18n.t('bookings.errors.min_end_date', { min })
                  )
                  .max(
                    getMaxDate(
                      today,
                      services.find(({ id }) => id === service)
                    ),
                    ({ max }) => I18n.t('bookings.errors.max_end_date', { max })
                  );
              }),
            count: yup
              .number()
              .when(
                ['rangeEndType', 'type', 'dayRange', 'everyNumberOf'],
                (
                  [
                    rangeEndType,
                    recurrenceType = '',
                    dayRange,
                    everyNumberOf = 1,
                  ]: [string?, string?, string?, number?, ...unknown[]],
                  schema: yup.NumberSchema
                ) => {
                  if (rangeEndType === 'date') return schema;

                  const minDate = getMinDate(date, recurrence);
                  const maxDate = getMaxDate(
                    today,
                    services.find(({ id }) => id === service)
                  );

                  const maxOccurrences = {
                    daily: Math.ceil(
                      (moment(maxDate).diff(moment(minDate), 'days', true) *
                        (dayRange === 'weekdays' ? 5 / 7 : 1)) /
                        everyNumberOf
                    ),
                    weekly: Math.ceil(
                      moment(maxDate).diff(moment(minDate), 'weeks', true) /
                        everyNumberOf
                    ),
                    monthly: Math.ceil(
                      moment(maxDate).diff(moment(minDate), 'months', true) /
                        everyNumberOf
                    ),
                  };

                  return schema
                    .required(I18n.t('bookings.errors.invalid_occurrences'))
                    .typeError(I18n.t('bookings.errors.invalid_occurrences'))
                    .integer(I18n.t('bookings.errors.invalid_occurrences'))
                    .min(2, ({ min }) =>
                      I18n.t('bookings.errors.min_occurrences', { min })
                    )
                    .max(
                      (maxOccurrences as Record<string, number>)[
                        recurrenceType
                      ],
                      ({ max }) =>
                        I18n.t('bookings.errors.max_occurrences', { max })
                    );
                }
              ),
          });
        }
      ),
  });

export default getSchema;

export const getValidator = createGetValidator(getSchema);
